import type {
  RepositoryIntelligenceRegistration,
  RepositoryIntelligenceStatus,
} from '@ashniva/types';
import { useQuery } from '@tanstack/react-query';

import { apiRequest } from '../../shared/lib/api-client';
import { intelligencePath, riKeys, riRetry } from './api';
import { classifyProblem } from './ri-problems';
import { isInFlight, repositoryState, type RepositoryState } from './ri-state';
import { usePollWindow } from './use-poll-window';

/** How often a registration in flight is looked at when the server suggests nothing. */
const IN_FLIGHT_POLL_MS = 5_000;
const IN_FLIGHT_LIFECYCLES = new Set(['approved', 'cloning', 'verifying', 'indexing']);
const IN_FLIGHT_REGISTRATIONS = new Set(['pending', 'approved', 'provisioning']);

const statusPollAfter = (data: RepositoryIntelligenceStatus | undefined) =>
  data?.lifecycle && IN_FLIGHT_LIFECYCLES.has(data.lifecycle.state) ? IN_FLIGHT_POLL_MS : null;
const registrationPollAfter = (data: RepositoryIntelligenceRegistration | undefined) =>
  data && IN_FLIGHT_REGISTRATIONS.has(data.registration.status)
    ? (data.registration.pollAfterMs ?? IN_FLIGHT_POLL_MS)
    : null;

export interface RepositoryStateResult {
  isLoading: boolean;
  /** A problem that leaves nothing to show (switched off, no access, unavailable…); null otherwise. */
  error: unknown;
  state: RepositoryState | null;
  status: RepositoryIntelligenceStatus | null;
  /** Following a registration in flight stopped after two minutes; `checkAgain` looks again. */
  stillWorking: boolean;
  checkAgain: () => void;
}

/**
 * One repository's state for the screens: the status, and the registration Desk asked for when the status cannot
 * say — Repository Intelligence does not serve the repository yet (404 or 409), or it failed and the registration
 * says why. While a registration is in flight, both are followed like every other piece of asynchronous work.
 * `follow` false (the lists): read once, never polled.
 */
export function useRepositoryState(linkId: string, follow = true): RepositoryStateResult {
  const statusPoll = usePollWindow(follow ? `${linkId}:status` : null, follow, statusPollAfter);
  const status = useQuery({
    queryKey: riKeys.status(linkId),
    queryFn: () => apiRequest<RepositoryIntelligenceStatus>(intelligencePath(linkId)),
    retry: riRetry,
    refetchInterval: statusPoll.refetchInterval,
  });
  const statusProblem = status.isError ? classifyProblem(status.error) : null;
  // Not served yet: the registration says where it is.
  const statusSilent = statusProblem === 'not-found' || statusProblem === 'conflict';
  const lifecycle = status.data?.lifecycle;
  const needsRegistration = statusSilent || lifecycle?.state === 'failed';

  const registrationPoll = usePollWindow(
    follow && needsRegistration ? `${linkId}:registration` : null,
    follow && needsRegistration,
    registrationPollAfter,
  );
  const registration = useQuery({
    queryKey: riKeys.registration(linkId),
    queryFn: () =>
      apiRequest<RepositoryIntelligenceRegistration>(`${intelligencePath(linkId)}/registration`),
    enabled: needsRegistration,
    retry: riRetry,
    refetchInterval: registrationPoll.refetchInterval,
  });
  const registrationProblem = registration.isError ? classifyProblem(registration.error) : null;
  // "Desk never asked" is simply "not indexed"; anything else about the registration leaves nothing to show.
  const registrationBlocks =
    registrationProblem !== null &&
    registrationProblem !== 'no-registration' &&
    registrationProblem !== 'not-found';

  const error: unknown =
    (status.isError && !statusSilent ? status.error : null) ??
    (registrationBlocks ? registration.error : null);
  const isLoading = status.isLoading || (needsRegistration && registration.isLoading);

  let state: RepositoryState | null = null;
  if (!error && !isLoading && (status.isSuccess || statusSilent)) {
    state = repositoryState(status.data ?? null, registration.data?.registration ?? null);
    if (!status.data && !registration.data && statusProblem === 'conflict') {
      // Being prepared, though Desk did not ask for it itself (an operator registered it).
      state = { kind: 'provisioning', step: 2 };
    }
  }
  const inFlight = state !== null && isInFlight(state);

  return {
    isLoading,
    error,
    state,
    status: status.data ?? null,
    stillWorking: inFlight && (statusPoll.stillWorking || registrationPoll.stillWorking),
    checkAgain: () => {
      statusPoll.restart();
      registrationPoll.restart();
      void status.refetch();
      if (needsRegistration) {
        void registration.refetch();
      }
    },
  };
}
